// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "../utils/axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem("webvenly_admin_token");

    if (token) {
      try {
        // Set axios default header
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        // Verify token with backend
        const response = await axios.get("/api/admin/verify");

        if (response.data.success) {
          setIsAdmin(true);
          setAdminData(response.data.admin);
        } else {
          // Token invalid, clear it
          localStorage.removeItem("webvenly_admin_token");
          delete axios.defaults.headers.common["Authorization"];
        }
      } catch (error) {
        console.error("Auth verification failed:", error);
        localStorage.removeItem("webvenly_admin_token");
        delete axios.defaults.headers.common["Authorization"];
      }
    }

    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post("/api/admin/login", {
        username,
        password,
      });

      if (response.data.success) {
        const { token, admin, expiresAt } = response.data;

        // Store token
        localStorage.setItem("webvenly_admin_token", token);
        localStorage.setItem("webvenly_admin_expires", expiresAt);

        // Set axios default header
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        // Update state
        setIsAdmin(true);
        setAdminData(admin);

        return { success: true };
      }

      return {
        success: false,
        error: response.data.error || "Login failed",
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Invalid credentials",
      };
    }
  };

  const logout = async () => {
    const token = localStorage.getItem("webvenly_admin_token");

    if (token) {
      try {
        // Notify backend
        await axios.post("/api/admin/logout");
      } catch (error) {
        console.error("Logout error:", error);
      }
    }

    // Clear local state and storage
    setIsAdmin(false);
    setAdminData(null);
    localStorage.removeItem("webvenly_admin_token");
    localStorage.removeItem("webvenly_admin_expires");
    delete axios.defaults.headers.common["Authorization"];
  };

  // Auto-logout on token expiry
  useEffect(() => {
    const checkExpiry = () => {
      const expiresAt = localStorage.getItem("webvenly_admin_expires");
      if (expiresAt && new Date(expiresAt) <= new Date()) {
        logout();
      }
    };

    const interval = setInterval(checkExpiry, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAdmin,
        adminData,
        login,
        logout,
        loading,
        checkAuthStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
